/**
 * This code is adapted from the US CDC, licensed CC0:
 * https://github.com/CDCgov/prime-simplereport/blob/main/frontend/src/app/VersionEnforcer.tsx
 */
import moment from 'moment';

import { reload } from './reload';

export const LOCAL_STORAGE_KEY = 'sparkplug::mostRecentVersionReload';
const RELOAD_THROTTLE_MINUTES = 15;

export class VersionService {
    public static async enforce() {
        if (
            !VersionService.localStorageIsAvailable() ||
            !import.meta.env.REACT_APP_CURRENT_COMMIT
        ) {
            return;
        }

        const mostRecentReload = VersionService.getMostRecentReload();
        if (VersionService.reloadedRecently(mostRecentReload)) {
            // We have reloaded too recently; get outta here
            return;
        }

        const remoteSHA = await VersionService.getSHA();
        // alternative to isPwa()
        if (document.querySelector('body')?.classList.contains('progressier-standalone')) {
            return;
        }
        if (import.meta.env.REACT_APP_CURRENT_COMMIT !== remoteSHA) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(new Date()));
            reload();
        }
    }

    public static registerPreloadErrorHandler() {
        window.addEventListener('vite:preloadError', VersionService.handlePreloadError);
    }

    public static handlePreloadError(event: Event) {
        event.preventDefault();

        if (!VersionService.localStorageIsAvailable()) {
            return;
        }

        const mostRecentReload = VersionService.getMostRecentReload();
        if (VersionService.reloadedRecently(mostRecentReload)) {
            return;
        }

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(new Date()));
        reload();
    }

    static getMostRecentReload(): Date | null {
        const serializedLastKnown = localStorage.getItem(LOCAL_STORAGE_KEY);
        return serializedLastKnown === null ? null : JSON.parse(serializedLastKnown);
    }

    /**
     * fetch the commit SHA deployed with the static assets and compare it to the
     * SHA encoded in the current build
     */
    public static async getSHA(): Promise<string> {
        if (import.meta.env.REACT_APP_CURRENT_COMMIT) {
            const result = await fetch(`${import.meta.env.REACT_APP_PUBLIC_URL}/static/commit.txt`);
            if (!result.ok) {
                throw result;
            }
            return (await result.text()).trim();
        }
        return '';
    }

    /**
     * from: https://stackoverflow.com/a/16427747/15155214
     */
    static localStorageIsAvailable() {
        const key = 'localStorageTestKey';
        try {
            localStorage.setItem(key, key);
            localStorage.removeItem(key);
            return true;
        } catch (e: any) {
            return false;
        }
    }

    private static reloadedRecently(mostRecentReload: Date | null) {
        return (
            mostRecentReload != null &&
            moment().isBefore(moment(mostRecentReload).add(RELOAD_THROTTLE_MINUTES, 'minutes'))
        );
    }
}
